import React from "react";
import { useNavigate } from "react-router-dom";
import "./MentionText.css";

/**
 * MentionText - Renders text with clickable @mentions and URLs
 * Reusable component for comments, replies, and other text content
 *
 * Supports two mention formats:
 *  - Structured tokens: @[Display Name](userId)  ← NEW
 *  - Legacy: @username
 *
 * @param {string} text - The text content to render
 * @param {boolean} linkToProfile - Whether @mentions should navigate to user profiles
 * @param {string} className - Optional additional CSS class
 */
const MentionText = ({ text, linkToProfile = true, className = "" }) => {
  const navigate = useNavigate();

  if (!text) return null;

  // URL regex pattern for matching http, https, and www URLs
  const urlPattern = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

  // Combined pattern: structured tokens, URLs, and legacy mentions
  // Structured tokens MUST come first in the alternation so they're matched before
  // the legacy regex can match fragments inside them.
  const combinedPattern =
    /(@\[[^\]]+\]\([^)]+\)|https?:\/\/[^\s]+|www\.[^\s]+|@[a-zA-Z0-9_]+)/gi;

  const parts = String(text).split(combinedPattern);

  const handleMentionClick = (usernameOrId, userId) => {
    if (linkToProfile) {
      // Use encodeURIComponent for display names with spaces
      const encoded = encodeURIComponent(userId || usernameOrId);
      navigate(`/profile/${encoded}`);
    }
  };

  return (
    <span className={`mention-text ${className}`}>
      {parts.map((part, idx) => {
        if (!part) return null;

        // Shared keyboard handler for mention links
        const handleMentionKeyDown = (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            // Extract userId if this is a structured token
            const structured = part.match(/^@\[([^\]]+)\]\(([^)]+)\)$/);
            const displayName = structured ? structured[1] : part.substring(1);
            const userId = structured ? structured[2] : undefined;
            handleMentionClick(displayName, userId);
          }
        };

        // Check if this part is a structured mention token: @[Name](userId)
        const structuredMatch = part.match(/^@\[([^\]]+)\]\(([^)]+)\)$/);
        if (structuredMatch) {
          const displayName = structuredMatch[1];
          const userId = structuredMatch[2];
          return (
            <span
              key={idx}
              className="mention-text__mention"
              data-mention={userId}
              onClick={(e) => {
                e.stopPropagation();
                handleMentionClick(displayName, userId);
              }}
              onKeyDown={handleMentionKeyDown}
              role="button"
              tabIndex={0}
              title={`Mentioned: ${displayName}`}
            >
              @{displayName}
            </span>
          );
        }

        // Check if this part is a URL (only render http/https links)
        if (urlPattern.test(part)) {
          urlPattern.lastIndex = 0;
          const href = part.startsWith("http") ? part : `https://${part}`;
          let safeHref = null;
          try {
            const parsed = new URL(href);
            if (parsed.protocol === "http:" || parsed.protocol === "https:") {
              safeHref = parsed.href;
            }
          } catch {
            safeHref = null;
          }
          if (!safeHref) {
            return <span key={idx}>{part}</span>;
          }
          return (
            <a
              key={idx}
              href={safeHref}
              target="_blank"
              rel="noopener noreferrer"
              className="mention-text__link"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        }

        // Check if this part is a legacy mention
        if (part.startsWith("@") && part.length > 1) {
          const username = part.substring(1);
          return (
            <span
              key={idx}
              className="mention-text__mention"
              data-mention={username}
              onClick={(e) => {
                e.stopPropagation();
                handleMentionClick(username);
              }}
              onKeyDown={handleMentionKeyDown}
              role="button"
              tabIndex={0}
            >
              {part}
            </span>
          );
        }

        // Regular text
        return <span key={idx}>{part}</span>;
      })}
    </span>
  );
};

export default MentionText;
